import { useEffect, useState } from 'react';
import { GridRowModel } from '@mui/x-data-grid-pro';
import {
  setAlert,
  useAppDispatch,
  useAppSelector,
  TableData,
  initializeTableData,
  resetTableData,
  isValueOption,
  ValueOption,
  generateColumns,
  updateTableRows,
  ExternalProviderMapping,
  useUpdateExternalProviderMappingMutation,
  GridData,
} from 'api';
import { AlertSeverity } from 'api/models/alert';
import { ActionType, DataGridTable } from 'app/common';
import { v4 as uuidv4 } from 'uuid';

interface ExternalProviderMappingProps {
  data?: GridData;
  isLoading: boolean;
  isFetching: boolean;
}

export const ExternalProviderMappingTable: React.FC<ExternalProviderMappingProps> =
  ({ data, isLoading, isFetching }) => {
    const [isRefetching, setIsRefetching] = useState<boolean>(false);
    const { currentMenuItem } = useAppSelector(state => state.menuItem);
    const mid = currentMenuItem?.id.toString() ?? '';
    const { rows, columns } = useAppSelector(state => state.dataGridTable);

    const [updateExternalMappingProvider, { isLoading: isCreating }] =
      useUpdateExternalProviderMappingMutation();
    const dispatch = useAppDispatch();

    useEffect(() => {
      dispatch(resetTableData());

      const tableData: TableData = {
        rows: data?.rows,
        columns: generateColumns(data?.columns),
      };

      dispatch(initializeTableData(tableData));
    }, [dispatch, data]);

    useEffect(() => {
      setIsRefetching(false);
    }, [mid]);

    const processRowUpdate = async (rowUpdate: GridRowModel) => {
      let updatedRow = { ...rowUpdate, isNew: false };
      const normalizedOrganizationTypeValueOptions = (
        columns.find(x => x.field === 'normalizedOrganizationType') as any
      )?.valueOptions;

      const normalizedOrganizationType: ValueOption = !isValueOption(
        (rowUpdate as ExternalProviderMapping).normalizedOrganizationType
      )
        ? normalizedOrganizationTypeValueOptions.find(
            (x: ValueOption) => x.value === rowUpdate.normalizedOrganizationType
          )
        : (rowUpdate as ExternalProviderMapping).normalizedOrganizationType;

      if (mid) {
        const reqBody = {
          ...(rowUpdate as ExternalProviderMapping),
          normalizedOrganizationType:
            normalizedOrganizationType && normalizedOrganizationType.value
              ? (normalizedOrganizationType.value as string)
              : undefined,
        };
        try {
          await updateExternalMappingProvider(reqBody)
            .unwrap()
            .then((resp: ExternalProviderMapping) => {
              if (rows) {
                const updatedRows = rows.map((row: ExternalProviderMapping) => {
                  if (row.npi === resp.npi) return { ...resp, id: uuidv4() };
                  return row;
                });
                dispatch(updateTableRows(updatedRows));
              }

              dispatch(
                setAlert({
                  message: 'Row updated.',
                  severity: AlertSeverity.Success,
                })
              );
            });
        } catch (err: any) {
          dispatch(
            setAlert({
              message: err.message,
              severity: AlertSeverity.Error,
            })
          );
          return;
        }
      }

      return updatedRow;
    };

    return (
      <DataGridTable
        cellActions={[ActionType.Edit]}
        processRowUpdate={processRowUpdate}
        isBusy={isFetching || isLoading || isCreating}
        toolbarProps={{ enableFilter: true }}
        isRefetching={isRefetching}
        noRowsError={data?.validationError}
      />
    );
  };
